// @ts-nocheck
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import useToggle from 'dpl/shared/hooks/useToggle';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import PuppyTransportOptionsModal from 'dpl/modals/PuppyTransportOptionsModal';
import WorkingWithBreederButton from 'dpl/components/BreederProfile/mobile/components/overview/WorkingWithBreederButton';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { breederTransportOptionsQueryDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { useBreederProfileData } from 'dpl/components/BreederProfile/mobile/hooks/useBreederProfileData';
import { FILTER_LOCATION_NAME } from 'dpl/constants/query_params';
import { TRANSPORT_DETAILS_TYPES } from 'dpl/modals/utils/constants';
import TransportDetailsModal from 'dpl/modals/TransportDetailsModal';
import Divider from 'dpl/components/BreederProfile/mobile/components/shared/Divider';

import Icon from './Icon';
import TransportationSectionSkeleton from '../skeletons/TransportationSectionSkeleton';
import TransportationSectionMap from './TransportationSectionMap';

const { HOME, AIRPORT, MEETUP } = TRANSPORT_DETAILS_TYPES;

const IS_LT_MD = isLessThanBreakpoint('md');
const ICON_SIZE = IS_LT_MD ? '22px' : '24px';

function getFirstTransportDetailsOptionByType(
  transportDetailsOptions: any[],
  transportType: string
) {
  // Do not show first meetup option if it is the meet-halfway option
  if (transportType === MEETUP) {
    const meetupOptions = transportDetailsOptions.filter(
      (option: any) => option.type === MEETUP
    );
    if (meetupOptions.length > 1 && meetupOptions[0].key === 'meet-halfway') {
      return meetupOptions[1];
    }
    return meetupOptions[0];
  }
  return transportDetailsOptions.filter(
    (option: any) => option.type === transportType
  )[0];
}

function getButtonCopyFromTransportType(transportType: string) {
  if (transportType === HOME) {
    return 'All pick-up details';
  } else if (transportType === AIRPORT) {
    return 'All airport options';
  }
  return 'All meet-up options';
}

interface TransportOptionProps {
  transportDetailsOption: {
    icon: string;
    title: string;
    subtitle?: string;
    type: string;
    key?: string;
  };
  setTransportDetailsModalType: (type: string) => void;
}

function TransportOption({
  transportDetailsOption,
  setTransportDetailsModalType
}: TransportOptionProps) {
  const isMeetHalfway =
    transportDetailsOption.key && transportDetailsOption.key === 'meet-halfway';

  return (
    <div className="flex items-center gap-16">
      <div className="ba b--default br3 secondary ph2 pv2 ph3-md pv3-md">
        <Icon
          name={transportDetailsOption.icon}
          width={ICON_SIZE}
          height={ICON_SIZE}
        />
      </div>
      <div className="flex flex-column gap-2 md:gap-4 font-14 md:font-18 lh-title">
        <p className="fw-medium default-color">
          {transportDetailsOption.title}
        </p>
        <div className="flex flex-wrap gap-2 md:gap-4">
          {transportDetailsOption.subtitle && (
            <p className="secondary">{transportDetailsOption.subtitle}</p>
          )}
          {transportDetailsOption.subtitle && !isMeetHalfway && (
            <p className="secondary"> · </p>
          )}
          {!isMeetHalfway && (
            <button
              type="button"
              className="underline stone-700"
              onClick={() =>
                setTransportDetailsModalType(transportDetailsOption.type)
              }
            >
              {getButtonCopyFromTransportType(transportDetailsOption.type)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

interface TransportationSectionProps {
  coordinates?: Array<{ lat: number; lng: number }>;
  sectionTitle?: string;
}

export default function TransportationSection({
  coordinates = [],
  sectionTitle = 'Location & pick-up'
}: TransportationSectionProps) {
  const [isTransportModalOpen, toggleTransportModalOpen] = useToggle(false);
  const [transportDetailsModalType, setTransportDetailsModalType] = useState<
    string | null
  >(null);

  const {
    server: { breederProfileId },
    queryParams: { [FILTER_LOCATION_NAME]: locName }
  } = useSelector(({ server, queryParams }: any) => ({ server, queryParams }));
  const { breeder, isFetchingBreeder } = useBreederProfileData();

  const {
    isFetching: isFetchingTransportOptions,
    value: {
      data: {
        sorted_transport_options: transportOptions,
        transport_details_options: transportDetailsOptions
      }
    }
  } = useResourceQuery(
    breederTransportOptionsQueryDefinition(breederProfileId, { locName })
  );

  if (isFetchingBreeder || isFetchingTransportOptions) {
    return <TransportationSectionSkeleton />;
  }

  const breederHomeOption = getFirstTransportDetailsOptionByType(
    transportDetailsOptions,
    HOME
  );
  const breederAirportOption = getFirstTransportDetailsOptionByType(
    transportDetailsOptions,
    AIRPORT
  );
  const breederMeetupOption = getFirstTransportDetailsOptionByType(
    transportDetailsOptions,
    MEETUP
  );

  const hasNoTransportOptions =
    isEmpty(breederAirportOption) &&
    isEmpty(breederMeetupOption) &&
    isEmpty(transportOptions);

  return (
    <div className="TransportationSection">
      <h3 className="font-28 md:font-40 lh-title mb2 mb4-md">{sectionTitle}</h3>
      <p className="font-14 md:font-18 secondary mb6 mb12-md">
        You can drive or fly to pick up your puppy from{' '}
        {breeder.user.first_name}.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-24 md:gap-96">
        <div className="flex flex-column gap-24 md:gap-32">
          <h3 className="font-20 md:font-24 lh-title">
            Breeder&apos;s location
          </h3>
          <TransportOption
            transportDetailsOption={breederHomeOption}
            setTransportDetailsModalType={setTransportDetailsModalType}
          />
          {!isEmpty(coordinates) && (
            <TransportationSectionMap coordinates={coordinates} />
          )}
        </div>
        {!hasNoTransportOptions && (
          <div className="flex flex-column gap-24 md:gap-32">
            <h3 className="font-20 md:font-24 lh-title">
              Other pick-up locations
            </h3>
            {!isEmpty(breederAirportOption) && (
              <TransportOption
                transportDetailsOption={breederAirportOption}
                setTransportDetailsModalType={setTransportDetailsModalType}
              />
            )}
            {!isEmpty(breederAirportOption) &&
              !isEmpty(breederMeetupOption) && (
                <Divider containerClassName="nv4" />
              )}
            {!isEmpty(breederMeetupOption) && (
              <TransportOption
                transportDetailsOption={breederMeetupOption}
                setTransportDetailsModalType={setTransportDetailsModalType}
              />
            )}
            {!isEmpty(transportOptions) && (
              <WorkingWithBreederButton
                title="Puppy transport options"
                subtitle={`${breeder.user.first_name} offers options to safely drop-off your new puppy.`}
                onClick={toggleTransportModalOpen}
              />
            )}
            <PuppyTransportOptionsModal
              breeder={{
                avatar_profile_photo_url: breeder.avatar_profile_photo_url,
                first_name: breeder.user.first_name,
                full_name: breeder.user.full_name,
                user_id: breeder.user.id,
                initials: breeder.initials
              }}
              transportOptions={transportOptions}
              isOpen={isTransportModalOpen}
              onClose={toggleTransportModalOpen}
            />
          </div>
        )}
        <TransportDetailsModal
          onCloseClick={() => setTransportDetailsModalType(null)}
          transportType={transportDetailsModalType}
          isOpen={!!transportDetailsModalType}
        />
      </div>
    </div>
  );
}
